import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Calendar,
  Download,
  RefreshCw,
} from 'lucide-react';

interface KPIMetric {
  id: string;
  title: string;
  value: number;
  change: number;
  changeType: 'increase' | 'decrease';
  isFavorable: boolean;
  format: 'currency' | 'percent' | 'number';
  icon: 'dollar' | 'users' | 'target';
}

interface ChartDataPoint {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
}

const ExecutiveDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);

  // Sample KPI data - in production, this would come from API
  const kpiMetrics: KPIMetric[] = [
    {
      id: 'revenue',
      title: 'Total Revenue',
      value: 2450000,
      change: 12.5,
      changeType: 'increase',
      isFavorable: true,
      format: 'currency',
      icon: 'dollar',
    },
    {
      id: 'profit',
      title: 'Net Profit',
      value: 485000,
      change: 8.3,
      changeType: 'increase',
      isFavorable: true,
      format: 'currency',
      icon: 'target',
    },
    {
      id: 'margin',
      title: 'Profit Margin',
      value: 19.8,
      change: -2.1,
      changeType: 'decrease',
      isFavorable: false,
      format: 'percent',
      icon: 'target',
    },
    {
      id: 'customers',
      title: 'Active Customers',
      value: 1248,
      change: 15.7,
      changeType: 'increase',
      isFavorable: true,
      format: 'number',
      icon: 'users',
    },
  ];

  // Sample chart data
  const revenueData: ChartDataPoint[] = [
    { period: 'Jan', revenue: 180000, expenses: 145000, profit: 35000 },
    { period: 'Feb', revenue: 195000, expenses: 150000, profit: 45000 },
    { period: 'Mar', revenue: 210000, expenses: 155000, profit: 55000 },
    { period: 'Apr', revenue: 225000, expenses: 160000, profit: 65000 },
    { period: 'May', revenue: 240000, expenses: 165000, profit: 75000 },
    { period: 'Jun', revenue: 255000, expenses: 170000, profit: 85000 },
  ];

  const categoryBreakdown = [
    { category: 'Subscription Revenue', amount: 1850000, percentage: 75.5 },
    { category: 'Professional Services', amount: 400000, percentage: 16.3 },
    { category: 'Other Revenue', amount: 200000, percentage: 8.2 },
  ];

  const expenseCategories = [
    { category: 'Sales & Marketing', amount: 735000, percentage: 30.0 },
    { category: 'R&D', amount: 612500, percentage: 25.0 },
    { category: 'G&A', amount: 490000, percentage: 20.0 },
    { category: 'Cost of Revenue', amount: 612500, percentage: 25.0 },
  ];

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatValue = (value: number, format: string): string => {
    switch (format) {
      case 'currency':
        return formatCurrency(value);
      case 'percent':
        return formatPercent(value);
      case 'number':
        return formatNumber(value);
      default:
        return String(value);
    }
  };

  const getIcon = (iconType: string) => {
    switch (iconType) {
      case 'dollar':
        return <DollarSign className="w-8 h-8" />;
      case 'users':
        return <Users className="w-8 h-8" />;
      case 'target':
        return <Target className="w-8 h-8" />;
      default:
        return <DollarSign className="w-8 h-8" />;
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Executive Dashboard</h1>
            <p className="text-neutral-600 mt-1">
              Real-time financial metrics and performance indicators
            </p>
          </div>

          <div className="flex items-center space-x-2">
            {/* Period Selector */}
            <div className="flex border border-neutral-300 rounded-lg overflow-hidden">
              {(['monthly', 'quarterly', 'yearly'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    selectedPeriod === period
                      ? 'bg-primary-500 text-white'
                      : 'bg-white text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>

            <button
              onClick={handleRefresh}
              className="btn btn-secondary"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button className="btn btn-primary">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 bg-neutral-50 overflow-y-auto">
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-6">
            {kpiMetrics.map((metric) => (
              <div key={metric.id} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`p-3 rounded-lg ${
                      metric.isFavorable
                        ? 'bg-success-100 text-success-600'
                        : 'bg-alert-100 text-alert-600'
                    }`}
                  >
                    {getIcon(metric.icon)}
                  </div>

                  <div
                    className={`flex items-center space-x-1 text-sm font-medium ${
                      metric.changeType === 'increase'
                        ? 'text-success-600'
                        : 'text-alert-600'
                    }`}
                  >
                    {metric.changeType === 'increase' ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span>{Math.abs(metric.change)}%</span>
                  </div>
                </div>

                <h3 className="text-sm font-medium text-neutral-600 mb-2">
                  {metric.title}
                </h3>

                <p className="text-3xl font-bold text-neutral-900">
                  {formatValue(metric.value, metric.format)}
                </p>

                <p className="text-xs text-neutral-500 mt-2">
                  vs. previous period
                </p>
              </div>
            ))}
          </div>

          {/* Revenue Trend Chart */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-neutral-900">
                Revenue & Profit Trend
              </h2>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-primary-500 rounded"></div>
                  <span className="text-neutral-600">Revenue</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-success-500 rounded"></div>
                  <span className="text-neutral-600">Profit</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-alert-500 rounded"></div>
                  <span className="text-neutral-600">Expenses</span>
                </div>
              </div>
            </div>

            {/* Simple bar chart visualization */}
            <div className="space-y-4">
              {revenueData.map((data, index) => (
                <div key={data.period}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-neutral-700 w-12">
                      {data.period}
                    </span>
                    <div className="flex-1 flex items-center space-x-2">
                      {/* Revenue Bar */}
                      <div className="flex-1 bg-neutral-100 rounded-full h-8 overflow-hidden relative">
                        <div
                          className="bg-primary-500 h-full flex items-center justify-end px-3"
                          style={{ width: `${(data.revenue / 300000) * 100}%` }}
                        >
                          <span className="text-xs font-medium text-white">
                            {formatCurrency(data.revenue)}
                          </span>
                        </div>
                      </div>

                      {/* Profit Indicator */}
                      <div className="w-24 text-right">
                        <span className="text-sm font-semibold text-success-600">
                          {formatCurrency(data.profit)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue Breakdown & Expenses */}
          <div className="grid grid-cols-2 gap-6">
            {/* Revenue Breakdown */}
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 mb-6">
                Revenue Breakdown
              </h2>

              <div className="space-y-4">
                {categoryBreakdown.map((item) => (
                  <div key={item.category}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-neutral-700">
                        {item.category}
                      </span>
                      <span className="text-sm font-semibold text-neutral-900">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-neutral-100 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-right mt-1">
                      <span className="text-xs text-neutral-500">
                        {formatPercent(item.percentage)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expense Categories */}
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 mb-6">
                Expense Categories
              </h2>

              <div className="space-y-4">
                {expenseCategories.map((item) => (
                  <div key={item.category}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-neutral-700">
                        {item.category}
                      </span>
                      <span className="text-sm font-semibold text-neutral-900">
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-neutral-100 rounded-full h-2">
                      <div
                        className="bg-alert-500 h-2 rounded-full"
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                    <div className="text-right mt-1">
                      <span className="text-xs text-neutral-500">
                        {formatPercent(item.percentage)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="card">
            <h2 className="text-lg font-semibold text-neutral-900 mb-6">
              Key Ratios & Metrics
            </h2>

            <div className="grid grid-cols-4 gap-6">
              <div className="p-4 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600 mb-1">Gross Margin</p>
                <p className="text-2xl font-bold text-neutral-900">75.0%</p>
                <p className="text-xs text-success-600 mt-1">+2.5% vs target</p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600 mb-1">EBITDA</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {formatCurrency(612500)}
                </p>
                <p className="text-xs text-success-600 mt-1">+8.3% MoM</p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600 mb-1">Cash Position</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {formatCurrency(3200000)}
                </p>
                <p className="text-xs text-neutral-600 mt-1">13 months runway</p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <p className="text-sm text-neutral-600 mb-1">Burn Rate</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {formatCurrency(245000)}
                </p>
                <p className="text-xs text-alert-600 mt-1">-15% vs last month</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
